import clsx from "clsx";
import { Field, Form, Formik } from "formik";
import { RadioGroup, TextField } from "formik-material-ui";
import { DatePicker, TimePicker } from "formik-material-ui-pickers";
import { graphql } from "gatsby";
import { navigate } from "gatsby-link";
import { Button } from "gatsby-theme-material-ui";
import PropTypes from "prop-types";
import React, { ReactElement, useRef, useState } from "react";

import DateFnsUtils from "@date-io/dayjs";
import {
  Container,
  FormControlLabel,
  LinearProgress,
  Radio,
  Theme,
  Typography
} from "@material-ui/core";
import { createStyles, makeStyles } from "@material-ui/core/styles";
import { MuiPickersUtilsProvider } from "@material-ui/pickers";

import Content, { HTMLContent } from "../components/Content";
import Layout from "../components/Layout";
import Helmet from "react-helmet";
import PreviewCompatibleImage from "../components/PreviewCompatibleImage";

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      display: "grid",
      gridArea: "content",
      marginBottom: theme.spacing(4),
      marginTop: theme.spacing(4),
      // [theme.breakpoints.down("sm")]: {
      //   gap: `${theme.spacing(4)}px`
      // },
      [theme.breakpoints.up("sm")]: {
        alignContent: "center",
        alignItems: "stretch",
        display: "grid",
        gap: `${theme.spacing(8)}px`,
        gridAutoFlow: "column",
        gridTemplateColumns: "3fr 2fr",
        paddingBottom: theme.spacing(4),
        paddingTop: theme.spacing(4)
      }
    },
    form: {
      display: "grid",
      gap: `${theme.spacing(2)}px`,
      paddingBottom: theme.spacing(4),
      paddingTop: theme.spacing(4)
    },
    overview: {
      alignContent: "center",
      backgroundColor: theme.palette.primary.dark,
      color: theme.palette.secondary.contrastText,
      display: "grid",
      padding: theme.spacing(8)
    },
    title: {
      fontSize: "16pt",
      fontWeight: "bold"
    },
    heading: {
      fontSize: "16pt",
      fontWeight: "bold"
    },
    subheading: {
      fontSize: "14pt",
      fontWeight: "bold"
    },
    description: {},
  })
);

function encode(data): string {
  return Object.keys(data)
    .map(key => encodeURIComponent(key) + "=" + encodeURIComponent(data[key]))
    .join("&");
}

export const LandlordsPageTemplate = ({
  title,
  heading,
  featuredimage,
  imagewidth,
  subheading,
  description,
  content,
  contentComponent
}): ReactElement => {
  const classes = useStyles();
  const PageContent = contentComponent || Content;

  const formRef = useRef(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [value, setValue] = React.useState("Buy");

  const [selectedDate, setSelectedDate] = React.useState<Date | null>(
    new Date("2014-08-18T21:11:54")
  );

  const handleChange = (
    event: React.ChangeEvent<{}>,
    newValue: string
  ): void => {
    // console.log("event", event);
    // console.log("newValue", newValue);
    setValue(newValue);
  };

  const handleDateChange = (date: Date | null): void => {
    setSelectedDate(date);
  };

  return (
    <Container className={clsx(classes.root)} component="main" fluid="fluid">
      <header>
        <Typography component="h1" variant="h4" className={classes.title}>
          {title}
        </Typography>
        {heading && (
          <Typography
            className={clsx(classes.heading)}
            component="h1"
            variant="h2"
          >
            {heading}
          </Typography>
        )}
        {subheading && (
          <Typography
            className={clsx(classes.subheading)}
            component="h2"
            variant="h3"
          >
            {subheading}
          </Typography>
        )}
        {featuredimage ? (
          <PreviewCompatibleImage
            imageInfo={{
              image: featuredimage,
              alt: heading,
              style: {
                width: `${imagewidth}px`,
                minWidth: '370px',
                maxWidth: '1024px',
                margin: "1rem auto",
              }
            }}
          />
        ) : null}
        {description && (
          <Typography className={clsx(classes.description)} component="p">
            {description}
          </Typography>
        )}
      </header>
      <section className={clsx(classes.segment, classes.overview)}>
        <PageContent className="content" content={content} />
      </section>
      <section className={clsx(classes.segment)}>
        <MuiPickersUtilsProvider utils={DateFnsUtils}>
          <Formik
            initialValues={{
              email: "",
              password: ""
            }}
            validate={(values): Partial => {
              const errors: Partial<Values> = {};

              if (!values.email) {
                errors.email = "Required";
              } else if (
                !/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,4}$/i.test(values.email)
              ) {
                errors.email = "Invalid email address";
              }

              return errors;
            }}
            onSubmit={(values): void => {
              const form = formRef.current;

              setIsSubmitting(true);

              fetch(form.getAttribute("action"), {
                method: "POST",
                headers: {
                  "Content-Type": "application/x-www-form-urlencoded"
                },
                body: encode({
                  "form-name": form.getAttribute("name"),
                  ...values
                })
              })
                .then(() => setIsSubmitting(false))
                .then(() => navigate(form.getAttribute("action")))
                .catch(error => alert(error));
            }}
          >
            {({ submitForm, isSubmitting }): ReactElement => (
              <Form
                action="/contact/thanks/"
                className={clsx(classes.form)}
                data-netlify-honeypot="bot-field"
                data-netlify="true"
                method="post"
                name="landlords"
                ref={formRef}
              >
                {/* The `form-name` hidden field is required to support form submissions without JavaScript */}
                <input type="hidden" name="form-name" value="contact" />

                <Typography component="legend" variant="h5">
                  Request a valuation of your property
                </Typography>

                <Field
                  component={RadioGroup}
                  aria-label="position"
                  className={classes.radioGroup}
                  name="property-type"
                  onChange={handleChange}
                  row
                  value={value}
                >
                  <FormControlLabel
                    control={<Radio />}
                    label="Sell"
                    labelPlacement="start"
                    value="Sell"
                  />
                  <FormControlLabel
                    control={<Radio />}
                    label="Let"
                    labelPlacement="start"
                    value="Let"
                  />
                </Field>

                <Field
                  component={TextField}
                  label="Name"
                  name="name"
                  type="name"
                  required
                />

                <Field
                  component={TextField}
                  label="Email"
                  name="email"
                  type="email"
                  required
                />

                <Field
                  component={TextField}
                  label="Telephone"
                  name="telephone"
                  type="tel"
                  pattern="[0-9]{3}-[0-9]{3}-[0-9]{4}"
                />

                <Field
                  component={DatePicker}
                  // margin="normal"
                  id="date-picker"
                  label="Date picker"
                  name="date-picker"
                  // format="MM/dd/yyyy"
                  value={selectedDate}
                  onChange={handleDateChange}
                  // KeyboardButtonProps={{
                  //   "aria-label": "change date"
                  // }}
                />
                <Field
                  component={TimePicker}
                  // margin="normal"
                  id="time-picker"
                  label="Time picker"
                  name="time-picker"
                  value={selectedDate}
                  onChange={handleDateChange}
                  // KeyboardButtonProps={{
                  //   "aria-label": "change time"
                  // }}
                />

                <Field
                  component={TextField}
                  name="email"
                  type="email"
                  label="Email"
                />

                {isSubmitting && <LinearProgress />}

                <Button
                  variant="contained"
                  // color="primary"
                  disabled={isSubmitting}
                  onClick={submitForm}
                >
                  Submit
                </Button>
              </Form>
            )}
          </Formik>
        </MuiPickersUtilsProvider>
      </section>
    </Container>
  );
};

LandlordsPageTemplate.propTypes = {
  title: PropTypes.string,
  heading: PropTypes.string,
  featuredimage: PropTypes.oneOfType([PropTypes.object, PropTypes.string]),
  imagewidth: PropTypes.string,
  subheading: PropTypes.string,
  description: PropTypes.string,
  content: PropTypes.string,
  contentComponent: PropTypes.func,
  helmet: PropTypes.object
};

const LandlordsPage = ({ data, location }): ReactElement => {
  const { markdownRemark: post } = data;
  const rootUrl = "https://williamslynch.co.uk";

  return (
    <Layout pathname={location.pathname}>
      <LandlordsPageTemplate
        title={post.frontmatter.title}
        heading={post.frontmatter.heading}
        subheading={post.frontmatter.subheading}
        description={post.frontmatter.description}
        featuredimage={post.frontmatter.featuredimage}
        imagewidth={post.frontmatter.imagewidth}
        contentComponent={HTMLContent}
        content={post.html}
        helmet={
          <Helmet titleTemplate="%s">
            <title>{`${post.frontmatter.title}`}</title>
            <meta name="description" content={`${post.frontmatter.description}`} />
            <meta name="image" content={`${rootUrl}${post.frontmatter.featuredimage?.childImageSharp.fluid.src}`} />
            <meta name="og:image" content={`${rootUrl}${post.frontmatter.featuredimage?.childImageSharp.fluid.src}`} />
            <meta name="twitter:image" content={`${rootUrl}${post.frontmatter.featuredimage?.childImageSharp.fluid.src}`} />
          </Helmet>
        }
      />
    </Layout>
  );
};

LandlordsPage.propTypes = {
  data: PropTypes.object.isRequired,
  location: PropTypes.object
};

export default LandlordsPage;

export const pageQuery = graphql`
  query LandlordsPage($id: String!) {
    markdownRemark(id: { eq: $id }) {
      html
      frontmatter {
        title
        heading
        subheading
        featuredimage {
          childImageSharp {
            fluid(maxWidth: 960, quality: 64) {
              ...GatsbyImageSharpFluid
            }
          }
        }
        imagewidth
        description
      }
    }
  }
`;
